<script setup lang="ts">
import {
  computed,
  onMounted,
  ref,
  toRefs,
} from 'vue';
import { useI18n } from 'vue-i18n';

import {
  GrammarMenuItem,
  GrammarQuaternaryMenuButton,
  GrammarTextField,
} from '@nora-monorepo/grammar-ui';

interface CustomNotificationsModalProps {
  selectedDateTime?: Date | null;
  minDate?: Date;
  maxDate?: Date;
  errors?: string | undefined;
}

const props = defineProps<CustomNotificationsModalProps>();
const emit = defineEmits<{
  (e: 'update', date: Date): void;
}>();
const { t } = useI18n();
const { selectedDateTime, minDate, maxDate } = toRefs(props);
const timeSlots = ref<string[]>([]);

const pad = (n: number) => n.toString().padStart(2, '0');

const selectedDate = computed(() => {
  if (!selectedDateTime.value) {
    return new Date();
  }
  const year = selectedDateTime.value.getFullYear();
  const month = selectedDateTime.value.getMonth();
  const day = selectedDateTime.value.getDate();

  return `${year}-${pad(month + 1)}-${pad(day)}`;
});

const selectedTime = computed(() => {
  if (!selectedDateTime.value) {
    return undefined;
  }
  const hours = pad(selectedDateTime.value.getHours());
  const minutes = pad(selectedDateTime.value.getMinutes());
  return `${hours}:${minutes}`;
});

function updateDate(newValue: string) {
  timeSlots.value = getTimeSlots();
  const date = new Date(newValue);
  if (selectedTime.value) {
    const [hour, minute] = selectedTime.value.split(':');
    if (hour !== undefined && minute !== undefined) {
      date.setHours(Number.parseInt(hour));
      date.setMinutes(Number.parseInt(minute));
    }
  }
  emit('update', date);
}

function updateTime(time: string) {
  if (selectedDateTime.value) {
    const date = new Date(selectedDateTime.value);
    const [hour, minute] = time.split(':');
    if (hour !== undefined && minute !== undefined) {
      date.setHours(Number.parseInt(hour));
      date.setMinutes(Number.parseInt(minute));
    }
    emit('update', date);
  }
}

function getTimeSlots(): string[] {
  const times: string[] = [];

  const minDateWithoutTime = minDate.value
    ? new Date(
        minDate.value.getFullYear(),
        minDate.value.getMonth(),
        minDate.value.getDate(),
      )
    : null;
  const date = new Date();

  for (let i = 0; i < 48; i++) {
    const hour = Math.floor(i / 2);
    const minute = (i % 2) * 30;

    const hourString = pad(hour);
    const minuteString = pad(minute);

    const time = `${hourString}:${minuteString}`;

    if (!selectedDateTime.value || !minDate.value || !minDateWithoutTime) {
      times.push(time);

      continue;
    }

    date.setHours(hour);
    date.setMinutes(minute);

    const selectedDateWithoutTime = new Date(
      selectedDateTime.value.getFullYear(),
      selectedDateTime.value.getMonth(),
      selectedDateTime.value.getDate(),
    );

    if (selectedDateWithoutTime > minDateWithoutTime) {
      times.push(time);

      continue;
    }

    if (date > minDate.value) {
      times.push(`${hourString}:${minuteString}`);
    }
  }

  return times;
}

onMounted(() => {
  timeSlots.value = getTimeSlots();
});
</script>

<template>
  <div class="flex flex-col justify-items-start gap-3">
    <GrammarTextField
      :value="selectedDate"
      input-type="date"
      :label="t('common.date')"
      placeholder="Select date"
      :min="minDate"
      :max="maxDate"
      :message="errors"
      :type="errors ? 'error' : 'default'"
      @input="updateDate"
    />
    <GrammarQuaternaryMenuButton
      :label="selectedTime ? `Time: ${selectedTime}` : 'Select time'"
    >
      <GrammarMenuItem
        v-for="(slot, index) in timeSlots"
        :key="index"
        :label="slot"
        @select="() => updateTime(slot)"
      />
    </GrammarQuaternaryMenuButton>
  </div>
</template>
